import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import GmailRecipientField, {
  emailsToTokens,
  tokensToValidEmails,
  tokensHaveInvalid,
} from '../Offers/TownPortalFlow/GmailRecipientField.js';
import logger from '../../../utils/logger';
import MuniDetailFieldPlaceholder from './MuniDetailFieldPlaceholder.js';
import { CARD_BORDER_RADIUS, colors, MUNI_DETAIL_INPUT_HEIGHT, muniDetailInputShellSx } from './constants.js';
import {
  contactEmailSuggestions,
  contactsDiffer,
  formatContactLine,
} from './utils.js';

function ContactRow({ label, contact, sx = {} }) {
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Typography
        sx={{
          fontSize: '0.68rem',
          fontWeight: 600,
          lineHeight: 1.2,
          color: colors.textSecondary,
          mb: 0.15,
          m: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.875rem',
          lineHeight: 1.35,
          color: colors.textPrimary,
          m: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {formatContactLine(contact)}
      </Typography>
    </Box>
  );
}

const OFFERS_CONTACT_INPUT_ID = 'muni-contact-offers-email';

function ContactEmailEditor({
  label,
  tokens,
  setTokens,
  suggestions,
  inputId,
  helperText,
  helperTextColor = colors.textSecondary,
}) {
  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: colors.borderTertiary,
          borderRadius: CARD_BORDER_RADIUS,
          bgcolor: colors.bgPrimary,
          overflow: 'hidden',
          px: 0.5,
          pb: 0.5,
        }}
      >
        <GmailRecipientField
          label={label}
          variant="to"
          tokens={tokens}
          onTokensChange={setTokens}
          suggestions={suggestions}
          placeholder="Add email"
          inputId={inputId}
        />
      </Box>
      {helperText && (
        <Typography
          sx={{
            fontSize: '0.68rem',
            lineHeight: 1.3,
            color: helperTextColor,
            fontStyle: 'italic',
            mt: 0.35,
            mb: 0,
            mx: 0,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function MunicipalityContacts({ contacts, isEditing, onEditingChange, onSaveTownContacts, loading = false }) {
  const [offersTokens, setOffersTokens] = useState([]);
  const [stipsTokens, setStipsTokens] = useState([]);
  const [showStipEditor, setShowStipEditor] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const suppressStipButtonClickRef = useRef(false);

  const showStipContact = useMemo(() => {
    if (!contacts?.stips?.emails?.length) return false;
    return contactsDiffer(contacts?.offers, contacts?.stips);
  }, [contacts]);
  const suggestions = useMemo(() => contactEmailSuggestions(contacts), [contacts]);

  const resetEditState = useCallback(() => {
    setOffersTokens(emailsToTokens(contacts?.offers?.emails ?? []));
    setStipsTokens(emailsToTokens(contacts?.stips?.emails ?? []));
    setShowStipEditor(showStipContact);
  }, [contacts, showStipContact]);

  useEffect(() => {
    if (isEditing) resetEditState();
  }, [isEditing, resetEditState]);

  useLayoutEffect(() => {
    if (!isEditing) return undefined;
    suppressStipButtonClickRef.current = true;
    document.getElementById(OFFERS_CONTACT_INPUT_ID)?.focus();
    const timer = window.setTimeout(() => {
      suppressStipButtonClickRef.current = false;
    }, 300);
    return () => window.clearTimeout(timer);
  }, [isEditing]);

  const hasInvalid = tokensHaveInvalid(offersTokens)
    || (showStipEditor && tokensHaveInvalid(stipsTokens));

  const handleCancel = () => {
    resetEditState();
    onEditingChange(false);
  };

  const handleSave = async () => {
    if (hasInvalid || isSaving || !onSaveTownContacts) return;

    const offersEmails = tokensToValidEmails(offersTokens);
    const stipEmails = showStipEditor ? tokensToValidEmails(stipsTokens) : undefined;

    setIsSaving(true);
    try {
      await onSaveTownContacts({
        negotiatorEmails: offersEmails,
        stipEmails,
      });
      onEditingChange(false);
    } catch (err) {
      logger.error('[MunicipalityContacts] save contacts error', err?.response?.data ?? err?.message ?? err);
    } finally {
      setIsSaving(false);
    }
  };

  const shellSx = {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    pb: 0.25,
    overflow: 'visible',
  };

  const cardSx = muniDetailInputShellSx();

  if (isEditing) {
    return (
      <Box sx={shellSx}>
        <Box sx={{ ...cardSx, px: 1, py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <ContactEmailEditor
            label="Offer Negotiators"
            tokens={offersTokens}
            setTokens={setOffersTokens}
            suggestions={suggestions}
            inputId={OFFERS_CONTACT_INPUT_ID}
          />

          {showStipEditor ? (
            <ContactEmailEditor
              label="Stipulation Contact"
              tokens={stipsTokens}
              setTokens={setStipsTokens}
              suggestions={suggestions}
              inputId="muni-contact-stips-email"
              helperText="The 'Stipulation Contact' is who unsigned stips are sent to. By default this is the offer negotiator. Only set this if custom stip handling is required."
              helperTextColor="#c5221f"
            />
          ) : (
            <Button
              size="small"
              disableRipple
              disableFocusRipple
              onClick={() => {
                if (suppressStipButtonClickRef.current) return;
                setStipsTokens([]);
                setShowStipEditor(true);
              }}
              sx={{
                alignSelf: 'flex-start',
                fontSize: '0.72rem',
                textTransform: 'none',
                px: 0.5,
                py: 0,
                minWidth: 0,
              }}
            >
              Add Custom Stip Contact
            </Button>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, pt: 0.25 }}>
            <Button size="small" onClick={handleCancel} sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={hasInvalid || isSaving}
              startIcon={<CheckIcon sx={{ fontSize: '0.9rem !important' }} />}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              {isSaving ? 'Saving...' : 'Save contacts'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return <MuniDetailFieldPlaceholder />;
  }

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...cardSx,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          pr: 14,
          minHeight: MUNI_DETAIL_INPUT_HEIGHT,
          maxHeight: MUNI_DETAIL_INPUT_HEIGHT,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            minWidth: 0,
            flex: 1,
          }}
        >
          <ContactRow
            label="Offer Negotiators"
            contact={contacts?.offers}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {showStipContact && (
            <ContactRow
              label="Stipulation Contact"
              contact={contacts?.stips}
              sx={{ flex: 1, minWidth: 0 }}
            />
          )}
        </Box>
        <Button
          size="small"
          variant="outlined"
          disableRipple
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onEditingChange(true)}
          startIcon={<EditIcon sx={{ fontSize: '0.9rem !important' }} />}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            fontSize: '0.72rem',
            textTransform: 'none',
            py: 0.25,
            px: 1,
            color: colors.textPrimary,
            borderColor: colors.textPrimary,
          }}
        >
          Edit Contacts
        </Button>
      </Box>
    </Box>
  );
}

export default React.memo(MunicipalityContacts);
